import { createStore } from "vuex";
import services from "@/services/services";

const messageTimeout = 10000;

class Request {
  constructor(name, code, progress, cancelCallback) {
    this.name = name;
    this.code = code;
    this.progress = progress;
    this.cancelCallback = cancelCallback;
    if (this.cancelCallback) this.cancellable = true;
    // Number of elements processed at the moment
    this.quantity = 0;

    // Get creation time
    this.creationTime = Date.now();
  }

  cancel() {
    if (this.cancellable) {
      this.cancelCallback();
    }
  }
}

export default createStore({
  state: {
    // Token for the backend, restored from the local storage so that reloading
    // the page keeps the user logged in
    token: localStorage.getItem("token"),
    messages: [], // List of messages for the user
    requests: [], // Request pending to the backend
    promptBeingProcessed: false, // True if a prompt is being processed
    // The request currently being streamed from the backend, filled in step
    // by step as its events arrive. Shaped like a request coming from
    // GET /requests/{id} so the history view can display it as is.
    liveRequest: null,
  },
  getters: {},
  mutations: {
    // login
    setToken(state, token) {
      state.token = token;

      // Kept in the local storage so the session survives a page reload. The
      // backend keeps it in the database on its side, so it stays valid.
      if (token) localStorage.setItem("token", token);
      else localStorage.removeItem("token");
    },
    // Messages
    sendMessage(state, msg) {
      // { type: "error", text: "Error message" }
      msg.id = services.uuid();
      state.messages.push(msg);
      setTimeout(() => {
        state.messages = state.messages.filter((m) => m.id != msg.id);
      }, messageTimeout);
    },
    removeMessage(state, msg) {
      state.messages = state.messages.filter((m) => m != msg);
    },

    // Requests
    startRequest(state, { name, code, progress, cancelCallback }) {
      let newRequest = new Request(name, code, progress, cancelCallback);
      state.requests.push(newRequest);
    },
    // startRequest(state, test) {
    //   // let newRequest = new Request(name, code, progress, cancelCallback);
    //   // state.requests.push(newRequest);
    // },
    endRequest(state, code) {
      state.requests = state.requests.filter((r) => r.code !== code);
    },
    updateRequestProgress(state, { code, progress }) {
      const request = state.requests.find((r) => r.code == code);
      if (!request) return;
      request.progress = progress;
    },
    updateRequestQuantity(state, { code, quantity }) {
      const request = state.requests.find((r) => r.code == code);
      if (!request) return;
      request.quantity = quantity;
    },
    cancelRequest(state, code) {
      const request = state.requests.find((r) => r.code == code);
      if (!request) return;
      request.cancel();
      // Remove request
      state.requests = state.requests.filter((r) => r.code !== code);
    },
    setPromptBeingProcessed(state, value) {
      state.promptBeingProcessed = value;
    },

    // Live request
    startLiveRequest(state, { prompt }) {
      state.liveRequest = {
        id: null,
        prompt,
        status: "pending",
        creationDate: (Date.now() / 1000).toString(),
        searchPassages: null,
        results: null,
        // Marks it as the one being streamed, so the history view can tell it
        // apart from the requests it loaded from the backend
        live: true,
        stage: "Preparing the search",
      };
    },
    applyLiveEvent(state, event) {
      const live = state.liveRequest;
      if (!live) return;

      switch (event.event) {
        case "request":
          live.id = event.id;
          live.creationDate = event.creationDate;
          live.stage = "Finding the subjects to search";
          break;

        case "searchPassages":
          live.searchPassages = event.searchPassages;
          live.stage = "Searching the lessons";
          break;

        case "slides":
          live.results = { ...(live.results || {}), slides: event.slides };
          live.stage = "Writing the answer";
          break;

        case "highlights": {
          // Each matched element gets its sentences back, scored, so the
          // slide view can shade the part that carried the match
          const slides = live.results?.slides;
          if (!slides) break;

          event.highlights.forEach((highlight) => {
            const slide = slides.find((s) => s.id === highlight.slideId);
            const element = slide?.elements?.[highlight.elementNumber];
            if (element) element.segments = highlight.segments;
          });
          break;
        }

        case "answerDelta":
          if (!live.results) live.results = {};
          live.results = {
            ...live.results,
            answer: (live.results.answer || "") + event.text,
          };
          live.stage = null;
          break;

        case "done":
          live.status = "success";
          live.stage = null;
          break;

        case "error":
          live.status = "failure";
          live.message = event.message;
          live.stage = null;
          break;
      }
    },
    failLiveRequest(state, message) {
      if (!state.liveRequest) return;
      state.liveRequest.status = "failure";
      state.liveRequest.stage = null;
      if (message) state.liveRequest.message = message;
    },
  },
  actions: {},
  modules: {},
});
